import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography, Card, Button, Alert, Input, Tag, Spin,
  Descriptions, Divider, message, Upload, Space
} from 'antd';
import {
  ScanOutlined, CheckCircleTwoTone, CloseCircleTwoTone,
  SafetyCertificateTwoTone, ReloadOutlined,
  BankOutlined, IdcardOutlined, WarningTwoTone,
  EyeOutlined, CopyOutlined, CameraOutlined,
  DownloadOutlined, MoneyCollectOutlined
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import QRCode from 'qrcode';
import jsQR from 'jsqr';
import { PLANS } from '../../data/plans';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface AdminQRISProps {
  token: string;
  API_URL: string;
}

export const AdminQRIS: React.FC<AdminQRISProps> = ({ token, API_URL }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrisString, setQrisString] = useState('');
  const [qrisPreviewUrl, setQrisPreviewUrl] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    merchantName: string | null;
    currencyCode?: string | null;
    currencyName?: string | null;
    message: string;
  } | null>(null);
  const [currentConfig, setCurrentConfig] = useState<{
    merchant_name: string;
    created_at: string;
    updated_at: string;
  } | null>(null);
  const [hasEnvFallback, setHasEnvFallback] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [dynamicLoading, setDynamicLoading] = useState(false);
  const [dynamicQRIS, setDynamicQRIS] = useState<{
    amount: number;
    qris_string: string;
    qr_data_url: string;
  } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/admin/qris/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.data) {
          setCurrentConfig(data.data);
          setHasEnvFallback(false);
        } else {
          setCurrentConfig(null);
          setHasEnvFallback(data.env_fallback || false);
        }
      }
    } catch (e) {
      console.error('Failed to fetch QRIS config', e);
    } finally {
      setLoading(false);
    }
  }, [token, API_URL]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  /**
   * Validate QRIS string and update state (validation result + preview).
   * Extracted as a standalone function so it can be called both from useEffect
   * (when user types) and directly after scanning a QR code from image upload.
   */
  const validateQRIS = useCallback((str: string) => {
    if (!str.trim()) {
      setValidationResult(null);
      setQrisPreviewUrl(null);
      return;
    }

    const trimmed = str.trim();
    // Check basic format
    if (!trimmed.startsWith('000201')) {
      setValidationResult({
        valid: false,
        merchantName: null,
        message: 'QRIS harus dimulai dengan "000201". Periksa kembali string QRIS Anda.'
      });
      setQrisPreviewUrl(null);
      return;
    }

    // Parse merchant name (tag 59)
    const merchantName = extractMerchantName(trimmed);
    if (!merchantName) {
      setValidationResult({
        valid: false,
        merchantName: null,
        message: 'Tidak dapat menemukan nama merchant (Tag 59) dalam QRIS string.'
      });
      setQrisPreviewUrl(null);
      return;
    }

    // Parse currency (tag 53)
    const currencyCode = extractCurrency(trimmed);
    const currencyName = currencyCode ? getCurrencyLabel(currencyCode) : null;

    // Validate merchant name
    const normalized = merchantName.toUpperCase().trim();
    const merchantValid = normalized.includes('IT SOLUTION');

    // Validate currency is IDR
    const currencyValid = currencyCode === '360';

    if (merchantValid && currencyValid) {
      setValidationResult({
        valid: true,
        merchantName,
        currencyCode,
        currencyName,
        message: `✓ Merchant: "${merchantName}" — sesuai IT SOLUTION. Mata uang: ${currencyName}.`
      });
    } else if (!merchantValid) {
      setValidationResult({
        valid: false,
        merchantName,
        currencyCode,
        currencyName,
        message: `Nama merchant QRIS adalah "${merchantName}", bukan IT SOLUTION.`
      });
    } else {
      // Merchant valid but currency invalid
      setValidationResult({
        valid: false,
        merchantName,
        currencyCode,
        currencyName,
        message: `Mata uang QRIS adalah ${currencyName}, bukan IDR (Rupiah). QRIS harus menggunakan Rupiah (kode 360). Hubungi bank penerbit.`
      });
    }

    // Generate preview QR code
    QRCode.toDataURL(trimmed, {
      width: 300,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' }
    }).then(url => {
      setQrisPreviewUrl(url);
    }).catch(() => {
      setQrisPreviewUrl(null);
    });
  }, []);

  // Real-time validation when QRIS string changes (user typing in textarea)
  useEffect(() => {
    validateQRIS(qrisString);
  }, [qrisString, validateQRIS]);

  const handleSave = async () => {
    if (!qrisString.trim()) return;

    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const res = await fetch(`${API_URL}/api/v1/admin/qris/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ qris_string: qrisString.trim() })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Gagal menyimpan konfigurasi QRIS');
      }

      setSuccessMsg(data.message || 'QRIS berhasil disimpan!');
      setCurrentConfig({
        merchant_name: data.data.merchant_name,
        created_at: data.data.created_at,
        updated_at: data.data.created_at
      });
      setHasEnvFallback(false);

      // Update preview with the server-generated one
      if (data.preview_qr) {
        setQrisPreviewUrl(data.preview_qr);
      }

      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err: any) {
      setErrorMsg(err.message);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Decode QR code from an uploaded image using jsQR.
   * Draws image onto a hidden canvas, extracts pixel data, and runs jsQR.
   */
  const decodeQRFromImage = async (file: File): Promise<string | null> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
          reject(new Error('Canvas tidak tersedia'));
          return;
        }

        // Scale down large images for performance
        const maxDim = 1024;
        let width = img.naturalWidth;
        let height = img.naturalHeight;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          reject(new Error('Canvas 2D context tidak tersedia'));
          return;
        }

        // Draw image and extract pixel data
        ctx.drawImage(img, 0, 0, width, height);
        // Clean up object URL to avoid memory leak
        URL.revokeObjectURL(img.src);

        const imageData = ctx.getImageData(0, 0, width, height);

        try {
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'attemptBoth',
          });

          if (code && code.data) {
            resolve(code.data);
          } else {
            // Try with inversion
            const inverted = new Uint8ClampedArray(imageData.data.length);
            for (let i = 0; i < imageData.data.length; i += 4) {
              inverted[i] = 255 - imageData.data[i];
              inverted[i + 1] = 255 - imageData.data[i + 1];
              inverted[i + 2] = 255 - imageData.data[i + 2];
              inverted[i + 3] = imageData.data[i + 3];
            }
            const invertedCode = jsQR(inverted, imageData.width, imageData.height);
            if (invertedCode && invertedCode.data) {
              resolve(invertedCode.data);
            } else {
              resolve(null);
            }
          }
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = () => {
        reject(new Error('Gagal memuat gambar'));
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    const fileObj = file as File;

    setScanning(true);
    try {
      const decoded = await decodeQRFromImage(fileObj);

      if (decoded && decoded.trim()) {
        const decodedStr = decoded.trim();
        // Set the string AND trigger validation immediately
        setQrisString(decodedStr);
        // Also run validation right away (double ensure — useEffect will also fire)
        validateQRIS(decodedStr);
        message.success({
          content: '✅ QR code berhasil di-decode! String QRIS terisi otomatis.',
          duration: 4,
          key: 'decode_success'
        });
      } else {
        message.warning({
          content: 'Tidak dapat mendecode QR code dari gambar. Paste string QRIS manual di kolom teks.',
          duration: 5,
          key: 'decode_fail'
        });
        // Still show the image as preview
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          setQrisPreviewUrl(dataUrl);
        };
        reader.readAsDataURL(fileObj);
      }

      onSuccess?.(null);
    } catch (err: any) {
      onError?.(err);
      message.error(err.message || 'Gagal memproses gambar');
    } finally {
      setScanning(false);
    }
  };

  const handleGenerateDynamic = async () => {
    setDynamicLoading(true);
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/v1/admin/qris/config/generate-dynamic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount: PLANS.monthly.price })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Gagal generate QRIS dinamis');
      }

      setDynamicQRIS(data.data);
      message.success({
        content: `✅ QRIS dinamis Rp ${PLANS.monthly.price.toLocaleString('id-ID')} (promo) berhasil digenerate!`,
        duration: 3,
        key: 'dynamic_success'
      });
    } catch (err: any) {
      setErrorMsg(err.message);
      message.error({
        content: err.message,
        duration: 5,
        key: 'dynamic_error'
      });
    } finally {
      setDynamicLoading(false);
    }
  };

  const handleDownloadQR = (dataUrl: string, amount: number) => {
    const link = document.createElement('a');
    link.download = `QRIS-Rp${amount.toLocaleString('id-ID').replace(/\./g, '-')}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyString = (qrisString: string) => {
    navigator.clipboard.writeText(qrisString).then(() => {
      message.success({
        content: '✅ QRIS string berhasil di-copy!',
        duration: 2,
        key: 'copy_qris'
      });
    }).catch(() => {
      message.error('Gagal copy ke clipboard');
    });
  };

  const handleCopy = () => {
    if (currentConfig?.merchant_name) {
      navigator.clipboard.writeText(currentConfig.merchant_name);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: '16px' }}>
        <Spin size="large" />
        <span style={{ fontWeight: 800 }}>Memuat konfigurasi QRIS...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px' }}>
      {/* Header */}
      <Card style={{
        border: '3px solid #1a202c',
        borderRadius: '16px',
        boxShadow: '4px 4px 0 #1a202c',
        marginBottom: '24px',
        background: 'linear-gradient(135deg, #e7f5ff 0%, #d0ebff 100%)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <Title level={3} style={{ margin: 0, fontWeight: 900, display: 'flex', alignItems: 'center' }}>
              <ScanOutlined style={{ marginRight: 10, fontSize: '28px', color: '#1a202c' }} />
              Konfigurasi QRIS — Admin
            </Title>
            <Paragraph style={{ margin: '4px 0 0 0', color: '#495057', fontWeight: 650 }}>
              Upload atau paste QRIS statis untuk pembayaran membership.{' '}
              <Tag color="red" style={{ fontWeight: 800 }}>Harus atas nama IT SOLUTION</Tag>
            </Paragraph>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchConfig}
            style={{
              background: '#1a202c',
              color: '#fff',
              border: '2px solid #1a202c',
              fontWeight: 800,
              borderRadius: '8px'
            }}
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Success / Error Messages */}
      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          closable
          onClose={() => setSuccessMsg('')}
          style={{ marginBottom: '16px', border: '2px solid #2b8a3e', borderRadius: '8px' }}
        />
      )}
      {errorMsg && (
        <Alert
          message={errorMsg}
          type="error"
          showIcon
          closable
          onClose={() => setErrorMsg('')}
          style={{ marginBottom: '16px', border: '2px solid #e03131', borderRadius: '8px' }}
        />
      )}

      {/* Current Config Summary */}
      {currentConfig && (
        <Card style={{
          border: '3px solid #2b8a3e',
          borderRadius: '16px',
          boxShadow: '4px 4px 0 #1a202c',
          marginBottom: '24px',
          background: '#f0fdf4'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: '24px' }} />
            <Title level={4} style={{ margin: 0, fontWeight: 900, color: '#2b8a3e' }}>
              QRIS Aktif
            </Title>
          </div>
          <Descriptions column={1} size="small" bordered
            style={{ backgroundColor: '#f8f9fa' }}
            labelStyle={{ fontWeight: 800, backgroundColor: '#e9ecef' }}
          >
            <Descriptions.Item label={<><IdcardOutlined /> Merchant</>}>
              <Tag color="green" style={{ fontWeight: 800, fontSize: '14px', padding: '2px 12px' }}>
                {currentConfig.merchant_name}
              </Tag>
              <Button
                type="text"
                size="small"
                icon={copied ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CopyOutlined />}
                onClick={handleCopy}
                style={{ marginLeft: 8 }}
              />
            </Descriptions.Item>
            <Descriptions.Item label={<><SafetyCertificateTwoTone twoToneColor="#52c41a" /> Status</>}>
              <Tag color="green" style={{ fontWeight: 700 }}><CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 4 }} /> Terverifikasi IT SOLUTION</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Terakhir Diperbarui">
              {new Date(currentConfig.updated_at || currentConfig.created_at).toLocaleString('id-ID')}
            </Descriptions.Item>
          </Descriptions>

          {/* Dynamic QRIS Generator */}
          <Divider style={{ borderTop: '2px solid #2b8a3e', marginTop: '20px' }}>
            <MoneyCollectOutlined style={{ marginRight: 6 }} /> Generate QRIS Dinamis
          </Divider>

          <div style={{ textAlign: 'center' }}>
            <Paragraph style={{ fontWeight: 600, color: '#495057', marginBottom: '12px' }}>
              Generate QR code dengan nominal <strong>Rp {PLANS.monthly.price.toLocaleString('id-ID')}</strong> (promo) —
              user langsung scan dan dapat QR dengan nominal terisi otomatis.
            </Paragraph>

            <Button
              type="primary"
              size="large"
              loading={dynamicLoading}
              onClick={handleGenerateDynamic}
              icon={<MoneyCollectOutlined />}
              style={{
                height: '48px',
                background: '#1a202c',
                border: '2px solid #1a202c',
                color: '#fff',
                fontWeight: 900,
                fontSize: '1rem',
                borderRadius: '10px',
                boxShadow: '3px 3px 0 #1a202c',
              }}
            >
              {dynamicLoading ? 'Mengenerate...' : `Generate QR Rp ${PLANS.monthly.price.toLocaleString('id-ID')} (Promo)`}
            </Button>

            {dynamicQRIS && (
              <div style={{ marginTop: '20px' }}>
                <Card style={{
                  display: 'inline-block',
                  border: '3px solid #1a202c',
                  borderRadius: '16px',
                  boxShadow: '4px 4px 0 #1a202c',
                  background: '#fff',
                  padding: '20px'
                }}>
                  {/* Amount Display */}
                  <div style={{
                    background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
                    borderRadius: '12px',
                    padding: '12px 24px',
                    marginBottom: '16px',
                    color: '#fff',
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, opacity: 0.7, letterSpacing: '1px', textTransform: 'uppercase' }}>
                      Total Pembayaran
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 900, letterSpacing: '1px' }}>
                      Rp {dynamicQRIS.amount.toLocaleString('id-ID')}
                    </div>
                  </div>

                  {/* QR Code Image */}
                  <img
                    src={dynamicQRIS.qr_data_url}
                    alt={`QRIS Rp ${dynamicQRIS.amount.toLocaleString('id-ID')}`}
                    style={{
                      width: '250px',
                      height: '250px',
                      objectFit: 'contain',
                      display: 'block',
                      margin: '0 auto',
                      border: '2px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '8px'
                    }}
                  />

                  {/* Merchant Info */}
                  <div style={{ marginTop: '12px', fontSize: '13px', fontWeight: 600, color: '#495057' }}>
                    <BankOutlined style={{ marginRight: 4 }} /> {currentConfig.merchant_name}
                  </div>

                  {/* Download & Copy Buttons */}
                  <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadQR(dynamicQRIS.qr_data_url, dynamicQRIS.amount)}
                      style={{
                        border: '2px solid #1a202c',
                        borderRadius: '8px',
                        fontWeight: 700,
                        background: '#f8f9fa'
                      }}
                    >
                      Download QR
                    </Button>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyString(dynamicQRIS.qris_string)}
                      style={{
                        border: '2px solid #1a202c',
                        borderRadius: '8px',
                        fontWeight: 700,
                        background: '#f8f9fa'
                      }}
                    >
                      Copy QRIS String
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Env Fallback Notice */}
      {hasEnvFallback && !currentConfig && (
        <Alert
          message={<><BankOutlined style={{ marginRight: 6 }} /> Menggunakan QRIS dari Environment Variable</>}
          description="Saat ini QRIS dikonfigurasi melalui variabel lingkungan QRIS_STATIC_STRING. Simpan konfigurasi baru melalui form di bawah untuk override."
          type="info"
          showIcon
          style={{ marginBottom: '16px', border: '2px solid #1a202c', borderRadius: '8px', background: '#fff3bf' }}
        />
      )}

      {/* Empty State */}
      {!currentConfig && !hasEnvFallback && (
        <Alert
          message={<><WarningTwoTone twoToneColor="#faad14" style={{ marginRight: 6 }} /> Belum Ada Konfigurasi QRIS</>}
          description="QRIS statis perlu dikonfigurasi agar user bisa melakukan pembayaran membership via QRIS. Paste QRIS string dari bank/fintech Anda di bawah."
          type="warning"
          showIcon
          style={{ marginBottom: '16px', border: '2px solid #f59f00', borderRadius: '8px' }}
        />
      )}

      {/* Hidden canvas for QR code decoding */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* QRIS Input Form */}
      <Card style={{
        border: '3px solid #1a202c',
        borderRadius: '16px',
        boxShadow: '4px 4px 0 #1a202c',
        marginBottom: '24px'
      }}>
        <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '12px', marginBottom: '20px' }}>
          <Title level={4} style={{ margin: 0, fontWeight: 900 }}>
            <ScanOutlined style={{ marginRight: 8 }} /> Upload / Paste QRIS
          </Title>
          <Paragraph style={{ margin: '4px 0 0 0', color: '#495057', fontWeight: 650 }}>
            Paste string QRIS statis (dimulai dengan <strong>000201</strong>) atau upload gambar QR code.{' '}
            <Tag color="red" style={{ fontWeight: 800 }}>WAJIB: Merchant = IT SOLUTION</Tag>
          </Paragraph>
        </div>

        {/* QR Code Image Upload */}
        <div style={{ marginBottom: '20px' }}>
          <Space direction="vertical" style={{ width: '100%' }}>                <Upload
              accept="image/png,image/jpeg,image/webp"
              showUploadList={false}
              customRequest={handleFileUpload}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('Hanya file gambar yang didukung (PNG, JPG, WebP)');
                  return Upload.LIST_IGNORE;
                }
                return true; // Allow customRequest to handle it
              }}
            >
              <Button
                icon={scanning ? <Spin /> : <CameraOutlined />}
                loading={scanning}
                style={{
                  border: '2px dashed #1a202c',
                  borderRadius: '8px',
                  fontWeight: 700,
                  height: '48px',
                  width: '100%',
                  background: '#f8f9fa'
                }}
              >
                {scanning ? 'Memindai QR Code...' : 'Upload Gambar QR Code (PNG/JPG)'}
              </Button>
            </Upload>
            <Text style={{ fontSize: '12px', color: '#868e96' }}>
              {scanning ? (
                <><Spin size="small" style={{ marginRight: 4 }} /> Sedang memindai QR code dari gambar...</>
              ) : (
                'Upload gambar QR code untuk scan otomatis, atau paste string QRIS manual di bawah'
              )}
            </Text>
          </Space>
        </div>

        {/* QRIS String Input */}
        <div style={{ marginBottom: '16px' }}>
          <TextArea
            rows={4}
            value={qrisString}
            onChange={(e) => setQrisString(e.target.value)}
            placeholder="Paste QRIS string di sini...&#10;Contoh: 00020101021126680014ID...&#10;(dimulai dengan 000201)"
            style={{
              border: '2px solid #1a202c',
              borderRadius: '8px',
              fontFamily: 'monospace',
              fontSize: '13px',
              padding: '12px'
            }}
          />
        </div>

        {/* Real-time Validation Result */}
        {validationResult && (
          <Alert
            message={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {validationResult.valid
                  ? <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: '18px' }} />
                  : <CloseCircleTwoTone twoToneColor="#f5222d" style={{ fontSize: '18px' }} />
                }
                <span style={{ fontWeight: 700 }}>{validationResult.message}</span>
              </div>
            }
            type={validationResult.valid ? 'success' : 'error'}
            showIcon={false}
            style={{
              marginBottom: '16px',
              border: `2px solid ${validationResult.valid ? '#2b8a3e' : '#e03131'}`,
              borderRadius: '8px',
              background: validationResult.valid ? '#f0fdf4' : '#fff5f5'
            }}
          />
        )}

        {/* QR Code Preview */}
        {qrisPreviewUrl && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <Divider style={{ borderTop: '2px solid #1a202c', fontWeight: 800, color: '#1a202c' }}>
              <EyeOutlined style={{ marginRight: 6 }} /> Preview QR Code
            </Divider>
            <div style={{
              display: 'inline-block',
              border: '3px solid #1a202c',
              borderRadius: '12px',
              padding: '12px',
              background: '#fff',
              boxShadow: '3px 3px 0 #1a202c'
            }}>
              <img
                src={qrisPreviewUrl}
                alt="QRIS Preview"
                style={{
                  width: '200px',
                  height: '200px',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>              {validationResult?.valid && (
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                <Tag color="green" style={{ fontWeight: 800, fontSize: '14px', padding: '4px 16px' }}>
                  <CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 6 }} />
                  Merchant: {validationResult.merchantName} ✓ IT SOLUTION
                </Tag>
                <Tag color="blue" style={{ fontWeight: 800, fontSize: '14px', padding: '4px 16px' }}>
                  <CheckCircleTwoTone twoToneColor="#1890ff" style={{ marginRight: 6 }} />
                  Mata Uang: {validationResult.currencyName} ✓
                </Tag>
              </div>
            )}
          </div>
        )}

        {/* Save Button */}
        <Button
          type="primary"
          block
          size="large"
          loading={saving}
          disabled={!validationResult?.valid}
          onClick={handleSave}
          style={{
            height: '50px',
            background: validationResult?.valid ? '#2b8a3e' : '#ced4da',
            border: '2px solid #1a202c',
            color: validationResult?.valid ? '#fff' : '#868e96',
            fontWeight: 900,
            fontSize: '1rem',
            borderRadius: '10px',
            boxShadow: validationResult?.valid ? '3px 3px 0 #1a202c' : 'none',
            cursor: validationResult?.valid ? 'pointer' : 'not-allowed'
          }}
          icon={<SafetyCertificateTwoTone twoToneColor={validationResult?.valid ? '#52c41a' : '#868e96'} />}
        >
          {validationResult?.valid
            ? 'Simpan Konfigurasi QRIS'
            : 'QRIS Harus Atas Nama IT SOLUTION'
          }
        </Button>
      </Card>

      {/* Guide */}
      <Card style={{
        border: '3px solid #1a202c',
        borderRadius: '16px',
        boxShadow: '4px 4px 0 #1a202c',
        background: '#f8f9fa'
      }}>
        <Title level={5} style={{ fontWeight: 900, marginBottom: '12px' }}>
          <BankOutlined style={{ marginRight: 8 }} /> Cara Mendapatkan QRIS Statis
        </Title>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '14px', fontWeight: 600, color: '#495057' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <Tag color="blue" style={{ fontWeight: 900, minWidth: '28px', textAlign: 'center' }}>1</Tag>
            <span>Hubungi bank/fintech Anda (BSI, BRI, Mandiri, BCA, etc.) untuk menerbitkan QRIS statis <strong>atas nama IT SOLUTION</strong>.</span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <Tag color="blue" style={{ fontWeight: 900, minWidth: '28px', textAlign: 'center' }}>2</Tag>
            <span>Bank akan memberikan QRIS statis berupa teks panjang (dimulai dengan <strong>000201</strong>) atau gambar QR code.</span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <Tag color="blue" style={{ fontWeight: 900, minWidth: '28px', textAlign: 'center' }}>3</Tag>
            <span>Paste teks QRIS di kolom di atas — sistem akan otomatis memvalidasi bahwa merchant adalah <strong>IT SOLUTION</strong>.</span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <Tag color="blue" style={{ fontWeight: 900, minWidth: '28px', textAlign: 'center' }}>4</Tag>
            <span>Klik <strong>Simpan Konfigurasi QRIS</strong> — QRIS akan langsung digunakan untuk semua pembayaran membership.</span>
          </div>
        </div>

        <Divider style={{ borderTop: '2px solid #1a202c', margin: '20px 0' }} />

        <Alert
          message={<span style={{ fontWeight: 800 }}><SafetyCertificateTwoTone twoToneColor="#f5222d" style={{ marginRight: 6 }} /> Validasi Merchant</span>}
          description={
            <span style={{ fontWeight: 600 }}>
              Sistem akan <strong>menolak</strong> QRIS yang bukan atas nama <strong>IT SOLUTION</strong>. 
              Pastikan merchant name pada QRIS statis Anda tercantum <strong>"IT SOLUTION"</strong>.
              Jika belum sesuai, hubungi bank penerbit untuk perubahan data merchant.
            </span>
          }
          type="warning"
          showIcon
          style={{ border: '2px solid #f59f00', borderRadius: '8px' }}
        />
      </Card>
    </div>
  );
};

/**
 * Parse a specific tag from QRIS EMV string.
 */
function extractTag(qrisString: string, targetTag: string): string | null {
  try {
    const str = qrisString.trim();
    let i = 0;
    while (i < str.length) {
      const tag = str.substring(i, i + 2);
      i += 2;
      if (i + 2 > str.length) break;
      const lenStr = str.substring(i, i + 2);
      i += 2;
      const len = parseInt(lenStr, 10);
      if (isNaN(len) || i + len > str.length) break;
      const value = str.substring(i, i + len);
      i += len;
      if (tag === targetTag) return value;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Extract merchant name (tag 59) from QRIS EMV string.
 */
function extractMerchantName(qrisString: string): string | null {
  return extractTag(qrisString, '59');
}

/**
 * Extract currency code (tag 53) from QRIS EMV string.
 */
function extractCurrency(qrisString: string): string | null {
  return extractTag(qrisString, '53');
}

/**
 * Get human-readable currency name from ISO 4217 code.
 */
function getCurrencyLabel(code: string): string {
  const currencies: Record<string, string> = {
    '360': 'IDR (Rupiah Indonesia)',
    '156': 'CNY (Yuan China)',
    '840': 'USD (Dolar Amerika)',
    '458': 'MYR (Ringgit Malaysia)',
    '702': 'SGD (Dolar Singapura)',
    '764': 'THB (Baht Thailand)',
    '608': 'PHP (Peso Filipina)',
  };
  return currencies[code] || `Kode ${code} (tidak dikenal)`;
}

export default AdminQRIS;
